import { signIn } from "@bindery/auth-consumer/browser";
import type { WorksheetGrade } from "@bindery/shared/contracts/worksheet";
import { Alert, AlertDescription } from "@bindery/ui/components/ui/alert";
import { Button } from "@bindery/ui/components/ui/button";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import { LoadingLines, Prompt } from "../chrome/signed-in";
import { Workbench } from "../chrome/workbench";
import { GradeForm } from "../components/grade-form";
import { useSession } from "../data/use-session";
import { useGradeWorksheet, useWorksheet } from "../data/use-worksheets";
import type { WorksheetRefusal } from "../data/worksheets-api";
import { formatDate } from "../lib/format-date";
import { gradePath, worksheetPath } from "../routes";

const REFUSALS: Record<WorksheetRefusal, string> = {
  not_found: "This worksheet isn’t in the signed-in reader’s list.",
  already_graded: "This worksheet was already graded.",
  invalid_token: "This grade link has been used or isn’t valid.",
  too_many_attempts: "Too many tries with this link. Wait a while first.",
  token_grading_off: "Grade links are turned off.",
};

type Outcome =
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "graded"; gradedAt: number; responseCount: number }
  | { kind: "refused"; reason: WorksheetRefusal }
  | { kind: "failed" };

function SignInToGrade({ worksheetId }: { worksheetId: string }) {
  return (
    <Prompt
      title="Sign in to grade this worksheet"
      action={
        <Button onClick={() => signIn(gradePath(worksheetId))}>Sign in</Button>
      }
    >
      <p>
        Grading records which words the reader read, so it needs the adult who
        made the sheet, or a grade link that still works.
      </p>
    </Prompt>
  );
}

function GradeContent({
  worksheetId,
  token,
}: {
  worksheetId: string;
  token: string | undefined;
}) {
  const session = useSession();
  const worksheet = useWorksheet(worksheetId);
  const grade = useGradeWorksheet(worksheetId, token);
  const [outcome, setOutcome] = useState<Outcome>({ kind: "idle" });

  const submit = (marks: WorksheetGrade) => {
    setOutcome({ kind: "busy" });
    grade(marks).then(
      (result) =>
        setOutcome(
          result.status === "graded"
            ? { kind: "graded", ...result }
            : { kind: "refused", reason: result.reason },
        ),
      () => setOutcome({ kind: "failed" }),
    );
  };

  if (session.status === "loading" || worksheet === undefined) {
    return <LoadingLines />;
  }
  if (outcome.kind === "graded") {
    return (
      <Prompt
        title="Graded"
        action={
          session.status === "signed-in" ? (
            <Button variant="outline" asChild>
              <Link to={worksheetPath(worksheetId)}>Back to the sheet</Link>
            </Button>
          ) : undefined
        }
      >
        <p>
          {outcome.responseCount} words recorded on{" "}
          {formatDate(outcome.gradedAt)}. The reader’s next sheet will build on
          them.
        </p>
      </Prompt>
    );
  }
  if (worksheet === null) return <SignInToGrade worksheetId={worksheetId} />;
  if (worksheet.gradedAt !== null) {
    return (
      <Prompt
        title="Already graded"
        action={
          <Button variant="outline" asChild>
            <Link to={worksheetPath(worksheetId)}>Back to the sheet</Link>
          </Button>
        }
      >
        <p>This worksheet was graded on {formatDate(worksheet.gradedAt)}.</p>
      </Prompt>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="font-serif text-3xl font-semibold tracking-tight">
          How did the reading go?
        </h1>
        <p className="text-muted-foreground max-w-prose">
          Mark the words the reader read on their own, then save. Each word
          becomes part of what the next worksheet is built from.
        </p>
      </div>
      {outcome.kind === "refused" ? (
        <Alert variant="destructive">
          <AlertDescription>
            {REFUSALS[outcome.reason]}
            {token === undefined ? null : " Sign in to grade instead."}
          </AlertDescription>
        </Alert>
      ) : null}
      {outcome.kind === "failed" ? (
        <Alert variant="destructive">
          <AlertDescription>
            The grades weren’t saved. Check the connection and try again.
          </AlertDescription>
        </Alert>
      ) : null}
      <GradeForm
        content={worksheet.content}
        onSubmit={submit}
        busy={outcome.kind === "busy"}
      />
    </div>
  );
}

export function GradePage() {
  const { worksheetId = "" } = useParams();
  const { hash } = useLocation();
  const token = hash.length > 1 ? hash.slice(1) : undefined;
  return (
    <Workbench>
      <GradeContent worksheetId={worksheetId} token={token} />
    </Workbench>
  );
}
